import React, { useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

/**
 * PaymentForm — rendered inside a Stripe <Elements> provider.
 * Handles card payments and Revolut Pay (shown automatically when enabled
 * in the Stripe Dashboard → Settings → Payment methods).
 *
 * Props:
 *   totalEur    {string}   - formatted display string, e.g. "19.99"
 *   onSuccess   {function} - called when payment confirmed successfully
 *   onBack      {function} - called when user clicks "Back"
 *   lang        {string}   - "en" | "bg"
 */
function PaymentForm({ totalEur, onSuccess, onBack, lang }) {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const labels = {
    en: {
      pay: `Pay €${totalEur}`,
      processing: "Processing…",
      back: "Back to details",
      errorFallback: "Payment failed. Please try again.",
    },
    bg: {
      pay: `Плати €${totalEur}`,
      processing: "Обработва се…",
      back: "Обратно към детайлите",
      errorFallback: "Плащането неуспешно. Моля, опитайте отново.",
    },
  };
  const s = labels[lang] || labels.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError("");

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message || s.errorFallback);
      setLoading(false);
      return;
    }

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Fallback redirect URL — needed for some methods (e.g. 3D Secure, bank redirects)
        return_url: `${window.location.origin}/cart?payment=success`,
      },
      // Don't redirect for card payments — stay on page and call onSuccess()
      redirect: "if_required",
    });

    if (stripeError) {
      setError(stripeError.message || s.errorFallback);
      setLoading(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Stripe Payment Element — Card, Google Pay, Apple Pay, Revolut Pay, PayPal */}
      <div className="rounded-xl overflow-hidden">
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
            paymentMethodOrder: ["card", "google_pay", "apple_pay", "revolut_pay", "paypal"],
          }}
        />
      </div>

      {error && (
        <p className="text-red-400 text-sm flex items-center space-x-1.5">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </p>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || loading}
        className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 text-white py-3 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg shadow-blue-900/30 flex items-center justify-center space-x-2"
      >
        {loading ? (
          <>
            <i className="fas fa-spinner fa-spin"></i>
            <span>{s.processing}</span>
          </>
        ) : (
          <>
            <i className="fas fa-lock"></i>
            <span>{s.pay}</span>
          </>
        )}
      </button>

      <button
        type="button"
        onClick={onBack}
        disabled={loading}
        className="w-full text-gray-400 hover:text-white text-sm py-2 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
      >
        <i className="fas fa-arrow-left text-xs"></i>
        <span>{s.back}</span>
      </button>
    </form>
  );
}

export default PaymentForm;
